/**
 * MANTENIMIENTO → INCIDENCIAS (FASE 8.2)
 *
 * «Hay un problema o necesidad técnica». En pantalla se llaman simplemente
 * «Incidencias» dentro de Mantenimiento, pero NO son las de Calidad: otra
 * API (`/api/mantenimiento/incidencias`), otros estados, otro historial.
 *
 * Una incidencia puede resolverse sin parte o generar uno o varios
 * («Crear parte de trabajo», CORRECTIVO sugerido). Crear el parte NO la
 * resuelve: lo decide quien gestiona.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type ActivoResumen,
  type EventoIm,
  type IncidenciaMntDetalle,
  type IncidenciaMntResumen,
  type OpcionesActivos,
  type ParteDetalle,
} from '../api.ts';
import { BarraAcciones, EmptyState, Error as ErrorCaja, fechaHora, FiltrosResponsive, Modal, Panel, SinResultados, useDatos, Vacio } from '../componentes/base.tsx';
import { EnlaceVolver, ErrorConEnlaces, EstadoImEtiqueta, EstadoPtEtiqueta, EvidenciasMnt, PrioridadEtiqueta, TIPOS_PT, useVolverAqui } from '../componentes/mantenimiento.tsx';
import { estadoIncidenciaMnt } from '../estados.ts';
import { PieHistorial, useHistorial } from '../componentes/historial.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useToast } from '../componentes/toast.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { AvisoDrill, chipsDrill, DRILL_VACIO, TEXTO_INDICADOR_IM } from '../componentes/indicadores-mnt.tsx';

const MIGAS = [{ texto: 'Mantenimiento' }, { texto: 'Incidencias', a: '/mantenimiento/incidencias' }];
/** UX-1C — feedback ligero de cada cambio de estado de la incidencia. */
const EXITO_INCIDENCIA: Readonly<Record<string, string>> = { EN_REVISION: 'Incidencia en revisión.', RESUELTA: 'Incidencia marcada como resuelta.', CANCELADA: 'Incidencia cancelada.' };
const PRIORIDADES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'] as const;
const etiquetaPrioridad = (p: string) => (p === 'CRITICA' ? 'Crítica' : p.charAt(0) + p.slice(1).toLowerCase());

function erroresDe(e: unknown): Record<string, string> {
  return e instanceof FalloApi ? (e.erroresDeCampo ?? {}) : {};
}

//  F12.4: además, los filtros del drill-down de Indicadores y `volver`.
const DEFAULTS = { q: '', estado: '', centro: '', prioridad: '', pagina: '1', ...DRILL_VACIO } as const;

export function PaginaIncidenciasMnt(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const lista = useListaUrl(DEFAULTS, 'pagina');
  const [alta, setAlta] = useState(false);
  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  const [q, estado, centro, prioridad] = [lista.valor('q'), lista.valor('estado'), lista.valor('centro'), lista.valor('prioridad')];
  const [indicador, pDesde, pHasta, idCliente] = [lista.valor('indicador'), lista.valor('pDesde'), lista.valor('pHasta'), lista.valor('idCliente')];
  const incidencias = useDatos(() => {
    const p = new URLSearchParams({ porPagina: '100' });
    if (q.trim() !== '') p.set('texto', q.trim());
    if (estado !== '') p.set('estado', estado);
    if (centro !== '') p.set('idCentro', centro);
    if (prioridad !== '') p.set('prioridad', prioridad);
    for (const [k, val] of [['indicador', indicador], ['pDesde', pDesde], ['pHasta', pHasta], ['idCliente', idCliente]] as const) if (val !== '') p.set(k, val);
    return api.get<{ filas: IncidenciaMntResumen[]; total: number }>(`/api/mantenimiento/incidencias?${p.toString()}`);
  }, [q, estado, centro, prioridad, indicador, pDesde, pHasta, idCliente]);
  const contador = [estado, centro, prioridad].filter((x) => x !== '').length;
  const chips = chipsDrill((k) => lista.valor(k), TEXTO_INDICADOR_IM);

  return (
    <Shell
      migas={[{ texto: 'Mantenimiento' }, { texto: 'Incidencias' }]}
      titulo="Incidencias de mantenimiento"
      acciones={puede('incidencias_mnt:crear') ? <button className="principal" onClick={() => setAlta(true)}>Nueva incidencia</button> : undefined}
    >
      <div className="panel">
        <div className="panel-cuerpo">
          <AvisoDrill volver={lista.valor('volver')} chips={chips} quitar={(c) => lista.fijarVarios(c.quitar as Partial<Record<keyof typeof DEFAULTS, string>>)} />
          <FiltrosResponsive contador={contador}
            busqueda={<input type="search" aria-label="Buscar por código o título" placeholder="Buscar IM o título…" value={q} onChange={(ev) => lista.fijar('q', ev.target.value)} />}>
            <div className="filtros-rejilla">
              <label className="campo"><span>Estado</span>
                <select value={estado} onChange={(ev) => lista.fijar('estado', ev.target.value)}>
                  <option value="">{indicador === '' ? 'Abiertas y en revisión' : 'Las del indicador'}</option>
                  <option value="ABIERTA">Abierta</option><option value="EN_REVISION">En revisión</option>
                  <option value="RESUELTA">Resuelta</option><option value="CANCELADA">Cancelada</option><option value="TODAS">Todas</option>
                </select>
              </label>
              <label className="campo"><span>Centro</span>
                <select value={centro} onChange={(ev) => lista.fijar('centro', ev.target.value)}>
                  <option value="">Todos</option>
                  {(opciones.datos?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
                </select>
              </label>
              <label className="campo"><span>Prioridad</span>
                <select value={prioridad} onChange={(ev) => lista.fijar('prioridad', ev.target.value)}>
                  <option value="">Todas</option>
                  {PRIORIDADES.map((p) => <option key={p} value={p}>{etiquetaPrioridad(p)}</option>)}
                </select>
              </label>
            </div>
          </FiltrosResponsive>
        </div>
      </div>
      <div className="panel">
        <Panel estado={incidencias}>
          {(d) => (d.filas.length === 0 ? (lista.esPorDefecto
            ? <EmptyState titulo="No hay incidencias abiertas." descripcion="Las averías o problemas comunicados sobre activos aparecerán aquí." />
            : <SinResultados limpiar={lista.limpiar} />) : (
            <ul className="lista-ots-movil lista-siempre" aria-label="Incidencias de mantenimiento">
              {d.filas.map((i) => (
                <li key={i.id}>
                  <Link to={`/mantenimiento/incidencias/${i.id}`} className="tarjeta-ot tarjeta-parte">
                    <div className="tarjeta-ot-cabecera"><span className="tarjeta-ot-codigo mono">{i.codigo}</span><EstadoImEtiqueta estado={i.estado} /></div>
                    <div className="tarjeta-ot-datos">
                      <span className="tarjeta-ot-fecha">{i.titulo}</span>
                      <span className="tarjeta-ot-secundario">{i.centro.nombre}{i.activo !== null ? ` · ${i.activo.nombre}` : ' · sin activo concreto'}</span>
                      <span className="fila" style={{ gap: 6 }}><PrioridadEtiqueta prioridad={i.prioridad} />{i.nPartes > 0 && <span className="etiqueta info">{i.nPartes} parte(s)</span>}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ))}
        </Panel>
      </div>
      {alta && opciones.datos !== null && (
        <FormAltaIncidencia opciones={opciones.datos} cerrar={() => setAlta(false)} alCrear={(id) => navegar(`/mantenimiento/incidencias/${id}`)} />
      )}
    </Shell>
  );
}

export function FormAltaIncidencia({ opciones, cerrar, alCrear, inicial = {} }: {
  opciones: OpcionesActivos; cerrar: () => void; alCrear: (id: string) => void; inicial?: { idCentro?: string; idActivo?: string };
}): ReactNode {
  const [idCentro, setIdCentro] = useState(inicial.idCentro ?? (opciones.centros.length === 1 ? opciones.centros[0]!.id : ''));
  const [idActivo, setIdActivo] = useState(inicial.idActivo ?? '');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [prioridad, setPrioridad] = useState('MEDIA');
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  const activos = useDatos(
    () => (idCentro === '' ? Promise.resolve({ filas: [] as ActivoResumen[], total: 0 }) : api.get<{ filas: ActivoResumen[]; total: number }>(`/api/activos?idCentro=${idCentro}&porPagina=200`)),
    [idCentro],
  );
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    try {
      const r = await api.post<IncidenciaMntDetalle>('/api/mantenimiento/incidencias', {
        idCentro, idActivo: idActivo === '' ? null : idActivo, titulo, descripcion: descripcion.trim() || null, prioridad,
      });
      alCrear(r.id);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <Modal titulo="Nueva incidencia de mantenimiento" cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Nueva incidencia de mantenimiento">
        <div className="campo">
          <label htmlFor="im-centro">Centro</label>
          <select id="im-centro" value={idCentro} required onChange={(ev) => { setIdCentro(ev.target.value); setIdActivo(''); }}>
            <option value="">Elige un centro…</option>
            {opciones.centros.map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="im-activo">Activo (opcional)</label>
          <select id="im-activo" value={idActivo} disabled={idCentro === ''} onChange={(ev) => setIdActivo(ev.target.value)}>
            <option value="">Sin activo concreto</option>
            {(activos.datos?.filas ?? []).map((a) => <option key={a.id} value={a.id}>{a.nombre}{a.titularidad === 'EMPRESA' ? ' (empresa prestadora)' : ''} · {a.codigo}</option>)}
          </select>
          {campos['idActivo'] !== undefined && <div className="error-campo">{campos['idActivo']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="im-titulo">¿Qué pasa?</label>
          <input id="im-titulo" required value={titulo} placeholder="Aire acondicionado no enfría" onChange={(ev) => setTitulo(ev.target.value)} />
          {campos['titulo'] !== undefined && <div className="error-campo">{campos['titulo']}</div>}
        </div>
        <div className="campo"><label htmlFor="im-desc">Detalle (opcional)</label><textarea id="im-desc" rows={3} value={descripcion} onChange={(ev) => setDescripcion(ev.target.value)} /></div>
        <div className="campo">
          <label htmlFor="im-prio">Prioridad</label>
          <select id="im-prio" value={prioridad} onChange={(ev) => setPrioridad(ev.target.value)}>{PRIORIDADES.map((p) => <option key={p} value={p}>{etiquetaPrioridad(p)}</option>)}</select>
        </div>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila"><button type="submit" className="principal">Crear incidencia</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

/** `/mantenimiento/incidencias/nueva?idCentro=&idActivo=` — desde la ficha (QR) de un activo. */
export function PaginaNuevaIncidenciaMnt(): ReactNode {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  return (
    <Shell migas={[...MIGAS, { texto: 'Nueva' }]} titulo="Nueva incidencia">
      <Panel estado={opciones}>
        {(o) => (
          <FormAltaIncidencia opciones={o} cerrar={() => navegar(-1)} alCrear={(id) => navegar(`/mantenimiento/incidencias/${id}`, { replace: true })}
            inicial={{ idCentro: params.get('idCentro') ?? undefined, idActivo: params.get('idActivo') ?? undefined }} />
        )}
      </Panel>
    </Shell>
  );
}

type Dialogo = 'revision' | 'resolver' | 'cancelar' | 'crear-parte' | null;

export function PaginaIncidenciaMnt(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const navegar = useNavigate();
  const inc = useDatos(() => api.get<IncidenciaMntDetalle>(`/api/mantenimiento/incidencias/${encodeURIComponent(id)}`), [id]);
  const historial = useHistorial<EventoIm>(`/api/mantenimiento/incidencias/${encodeURIComponent(id)}/historial`);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [error, setError] = useState<unknown>(null);
  const [texto, setTexto] = useState('');
  const toast = useToast();
  const [textoPublico, setTextoPublico] = useState('');
  const [tipo, setTipo] = useState('CORRECTIVO');
  const [descripcionPt, setDescripcionPt] = useState('');
  const [prioridadPt, setPrioridadPt] = useState('MEDIA');
  const [busqueda, setBusqueda] = useSearchParams();
  const ubicacionActual = useLocation();
  const volverAqui = useVolverAqui(`la incidencia ${inc.datos?.codigo ?? ''}`.trim());
  //  F12.2: desde el parte («¿Resolver también la incidencia?») se llega con ?resolver=1: el diálogo se abre solo,
  //  con la propuesta de resolución. Una sola vez (el parámetro se quita sin perder el «Volver»).
  useEffect(() => {
    const d = inc.datos;
    if (d === null || busqueda.get('resolver') !== '1') return;
    if (d.porResolver === true && puede('incidencias_mnt:gestionar')) abrirResolver(d);
    const q = new URLSearchParams(busqueda);
    q.delete('resolver');
    setBusqueda(q, { replace: true, state: ubicacionActual.state });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inc.datos]);

  if (inc.error instanceof FalloApi && (inc.error.estado === 404 || inc.error.estado === 403)) {
    return <Shell migas={MIGAS} titulo="Incidencia no disponible"><div className="panel"><Vacio titulo="Esta incidencia no existe o no tienes acceso" /></div></Shell>;
  }
  const i = inc.datos;
  const gestiona = puede('incidencias_mnt:gestionar');
  const viva = i !== null && (i.estado === 'ABIERTA' || i.estado === 'EN_REVISION');
  const base = `/api/mantenimiento/incidencias/${id}`;
  /** F12.2 — resolver con la propuesta (lo hecho en sus partes), editable. */
  function abrirResolver(d: IncidenciaMntDetalle): void {
    setTexto(d.propuestaResolucion?.resolucion ?? '');
    setTextoPublico(d.propuestaResolucion?.resolucionPublica ?? '');
    setDialogo('resolver');
  }

  async function estado(nuevo: string, extra: Record<string, unknown> = {}): Promise<void> {
    setError(null);
    try {
      await api.post(`${base}/estado`, { estado: nuevo, ...extra });
      setDialogo(null);
      setTexto('');
      inc.recargar();
      historial.recargar();
      const hecho = EXITO_INCIDENCIA[nuevo];
      if (hecho !== undefined) toast.exito(hecho);
    } catch (e) {
      setError(e);
    }
  }
  async function crearParte(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    try {
      const r = await api.post<{ parte: ParteDetalle }>(`${base}/crear-parte`, {
        tipo,
        ...(texto.trim() !== '' && texto.trim() !== i?.titulo ? { titulo: texto.trim() } : {}),
        ...(descripcionPt.trim() !== (i?.descripcion ?? '') ? { descripcion: descripcionPt.trim() || null } : {}),
        ...(prioridadPt !== i?.prioridad ? { prioridad: prioridadPt } : {}),
      });
      navegar(`/mantenimiento/partes/${r.parte.id}`, { state: volverAqui });
    } catch (e) {
      setError(e);
    }
  }

  return (
    <Shell
      migas={[...MIGAS, { texto: i?.codigo ?? '…' }]}
      titulo={i?.titulo ?? 'Incidencia'}
      estado={i === null ? undefined : <EstadoImEtiqueta estado={i.estado} />}
      meta={i === null ? undefined : <><span className="mono">{i.codigo}</span><PrioridadEtiqueta prioridad={i.prioridad} />{i.origen === 'CLIENTE' && <span className="etiqueta info">Abierta por el cliente</span>}</>}
      acciones={i === null || !gestiona || !viva ? undefined : (() => {
        //  UX-1B: principal = crear el parte (o, sin ese permiso, resolver); revisar/resolver detrás; cancelar en «Más acciones».
        const crear = puede('partes:gestionar') ? <button className={i.porResolver === true ? undefined : 'principal'} onClick={() => {
          setTexto(i.titulo); setDescripcionPt(i.descripcion ?? ''); setPrioridadPt(i.prioridad); setTipo('CORRECTIVO'); setDialogo('crear-parte');
        }}>Crear parte de trabajo</button> : null;
        //  F12.2: con todo el trabajo terminado, resolver pasa a ser lo principal (y viene con la propuesta).
        const resolver = <button className={crear === null || i.porResolver === true ? 'principal' : undefined} onClick={() => abrirResolver(i)}>Marcar resuelta</button>;
        return (
          <BarraAcciones etiqueta="Acciones de la incidencia"
            principal={i.porResolver === true || crear === null ? resolver : crear}
            secundaria={crear === null ? undefined : i.porResolver === true ? crear : resolver}
            mas={[
              i.estado === 'ABIERTA' && { texto: 'Poner en revisión', alPulsar: () => void estado('EN_REVISION') },
              { texto: 'Cancelar incidencia', alPulsar: () => { setTexto(''); setDialogo('cancelar'); }, peligro: true },
            ]} />
        );
      })()}
    >
      <EnlaceVolver />
      {error !== null && <ErrorConEnlaces error={error} />}
      <Panel estado={inc}>
        {(d) => (
          <>
            {d.porResolver === true && gestiona && (
              <div className="nota exito" role="status">
                <strong>Todos los trabajos relacionados están terminados.</strong> ¿Resolver también la incidencia?{' '}
                <button type="button" className="principal" onClick={() => abrirResolver(d)}>Resolver la incidencia</button>
              </div>
            )}
            {d.vueltaARevision != null && (
              <div className="nota aviso" role="status">
                <strong>Ha vuelto a revisión</strong> el {fechaHora(d.vueltaARevision.en)} al reabrirse
                {d.vueltaARevision.parte === null ? ' un parte suyo' : <> el parte <Link to={`/mantenimiento/partes/${d.vueltaARevision.parte.id}`} state={volverAqui}>{d.vueltaARevision.parte.codigo}</Link></>}.
                {d.vueltaARevision.resolucionAnterior !== null && <div className="pequena">Resolución anterior (ya no vigente): {d.vueltaARevision.resolucionAnterior}</div>}
              </div>
            )}
            {d.estado === 'RESUELTA' && <div className="nota exito" role="status"><strong>Resuelta</strong> por {d.resueltoPor?.nombre} · {fechaHora(d.resueltoEn)}. {d.resolucion}{d.resolucionPublica !== null && <div className="pequena">Respuesta al cliente: {d.resolucionPublica}</div>}</div>}
            {d.estado === 'CANCELADA' && <div className="nota error" role="status"><strong>Cancelada.</strong> {d.motivoCancelacion}</div>}
            <div className="panel">
              <div className="panel-cabecera"><h2>Información</h2></div>
              <div className="panel-cuerpo datos">
                <div className="dato"><div className="etiqueta-dato">Cliente</div><div className="valor-dato">{d.cliente.nombre}</div></div>
                <div className="dato"><div className="etiqueta-dato">Centro</div><div className="valor-dato">{d.centro.nombre}</div></div>
                <div className="dato"><div className="etiqueta-dato">Activo</div><div className="valor-dato">{d.activo === null ? 'Sin activo concreto' : <Link to={`/activos/${d.activo.id}`} state={volverAqui}>{d.activo.nombre}</Link>}</div></div>
                <div className="dato"><div className="etiqueta-dato">Reportada</div><div className="valor-dato">{d.creadoPor.nombre} · {fechaHora(d.creadoEn)}</div></div>
              </div>
              {d.descripcion !== null && <div className="panel-cuerpo"><p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{d.descripcion}</p></div>}
            </div>
            <section className="panel" aria-label="Partes de trabajo">
              <div className="panel-cabecera"><h2>Partes de trabajo</h2></div>
              <div className="panel-cuerpo">
                {d.partes.length === 0 ? <p className="suave">Ningún parte todavía. Una incidencia también puede resolverse sin parte.</p> : (
                  <ul>{d.partes.map((p) => <li key={p.id} className="fila"><Link to={`/mantenimiento/partes/${p.id}`} state={volverAqui}>{p.titulo}</Link> <span className="suave pequena">{TIPOS_PT[p.tipo]} · {p.codigo}</span> <EstadoPtEtiqueta estado={p.estado} /></li>)}</ul>
                )}
              </div>
            </section>
            <EvidenciasMnt rutaLista={`${base}/evidencias`} rutaSubida={`${base}/evidencias`} puedeSubir={viva && puede('incidencias_mnt:crear')} puedeAnular={gestiona} puedePublicar={gestiona && d.origen === 'CLIENTE'}
              motivoNoPublicable={!gestiona ? 'Uso interno: sólo quien gestiona puede publicar evidencias al cliente.' : 'Uso interno: esta incidencia es interna; el cliente no la ve.'} contextos={['ANTES', 'GENERAL', 'DOCUMENTO']} />
          </>
        )}
      </Panel>
      <section className="panel" aria-label="Historial">
        <div className="panel-cabecera"><h2>Historial</h2></div>
        <div className="panel-cuerpo">
          <Panel estado={historial}>
            {(h) => (
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                {h.filas.map((e) => (
                  <li key={e.id} style={{ marginBottom: 6 }}>
                    <strong>{e.tipo === 'ALTA' ? 'Alta' : e.tipo === 'ESTADO' ? `Estado: ${estadoIncidenciaMnt(e.estadoAnterior).label} → ${estadoIncidenciaMnt(e.estadoNuevo).label}` : e.tipo === 'PT_VINCULADO' ? `Parte ${e.parte ?? ''} vinculado` : 'Datos editados'}</strong>
                    {e.motivo !== null && <div className="pequena">{e.motivo}</div>}
                    {/* F12.2: al volver a revisión, la resolución anterior queda aquí, en el historial. */}
                    {typeof e.cambios?.['resolucionAnterior'] === 'string' && <div className="pequena suave">Resolución anterior: {String(e.cambios['resolucionAnterior'])}</div>}
                    <div className="suave pequena">{e.usuario.nombre} · {fechaHora(e.ocurridoEn)}</div>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
          <PieHistorial historial={historial} />
        </div>
      </section>

      {dialogo === 'crear-parte' && i !== null && (
        <Modal titulo="Crear parte de trabajo" cerrar={() => setDialogo(null)}>
          <form onSubmit={(ev) => void crearParte(ev)} aria-label="Crear parte de trabajo">
            {/* F12.2: lo que ya se sabe, a la vista y sin volver a pedirlo. */}
            <dl className="contexto-alta">
              <div><dt>Centro</dt><dd>{i.centro.nombre}</dd></div>
              <div><dt>Activo</dt><dd>{i.activo === null ? 'Sin activo concreto' : i.activo.nombre}</dd></div>
              {i.activo?.ubicacion != null && <div><dt>Ubicación</dt><dd>{i.activo.ubicacion}</dd></div>}
            </dl>
            <p className="suave pequena">La incidencia no cambia de estado: cuando el trabajo termine se te ofrecerá resolverla.</p>
            <div className="campo">
              <label htmlFor="cp-tipo">Tipo</label>
              <select id="cp-tipo" value={tipo} onChange={(ev) => setTipo(ev.target.value)}>{/* F12.1: un parte nacido de una incidencia nunca es preventivo (ésos los genera su plan). */}
                {Object.entries(TIPOS_PT).filter(([k]) => k !== 'PREVENTIVO').map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select>
            </div>
            <div className="campo"><label htmlFor="cp-titulo">Título</label><input id="cp-titulo" required value={texto} onChange={(ev) => setTexto(ev.target.value)} /></div>
            <div className="campo"><label htmlFor="cp-descripcion">Qué hay que hacer</label><textarea id="cp-descripcion" rows={3} value={descripcionPt} onChange={(ev) => setDescripcionPt(ev.target.value)} /></div>
            <div className="campo">
              <label htmlFor="cp-prioridad">Prioridad</label>
              <select id="cp-prioridad" value={prioridadPt} onChange={(ev) => setPrioridadPt(ev.target.value)}>
                {PRIORIDADES.map((p) => <option key={p} value={p}>{etiquetaPrioridad(p)}</option>)}
              </select>
            </div>
            <div className="fila"><button type="submit" className="principal">Crear parte</button><button type="button" onClick={() => setDialogo(null)}>Cancelar</button></div>
          </form>
        </Modal>
      )}
      {(dialogo === 'resolver' || dialogo === 'cancelar') && (
        <Modal titulo={dialogo === 'resolver' ? 'Marcar resuelta' : 'Cancelar incidencia'} cerrar={() => setDialogo(null)}>
          <form aria-label={dialogo === 'resolver' ? 'Marcar resuelta' : 'Cancelar incidencia'} onSubmit={(ev) => {
            ev.preventDefault();
            void estado(dialogo === 'resolver' ? 'RESUELTA' : 'CANCELADA', dialogo === 'resolver'
              ? { resolucion: texto, ...(textoPublico.trim() !== '' ? { resolucionPublica: textoPublico.trim() } : {}) }
              : { motivoCancelacion: texto });
          }}>
            <div className="campo">
              <label htmlFor="im-texto">{dialogo === 'resolver' ? '¿Cómo se ha resuelto?' : 'Motivo'}</label>
              <textarea id="im-texto" rows={3} value={texto} onChange={(ev) => setTexto(ev.target.value)} />
            </div>
            {dialogo === 'resolver' && i?.origen === 'CLIENTE' && (
              <div className="campo">
                <label htmlFor="im-publico">Respuesta para el cliente (opcional)</label>
                <textarea id="im-publico" rows={2} value={textoPublico} onChange={(ev) => setTextoPublico(ev.target.value)} />
                <p className="suave pequena">Es lo único de la resolución que verá el cliente. Sin ella verá «Resuelta por el equipo de mantenimiento».</p>
              </div>
            )}
            <div className="fila"><button type="submit" className="principal" disabled={texto.trim().length < 5}>Confirmar</button><button type="button" onClick={() => setDialogo(null)}>Volver</button></div>
          </form>
        </Modal>
      )}
    </Shell>
  );
}
